import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, BellOff, BellRing } from "lucide-react";

/**
 * ScoreNotifier — opt-in browser notifications on Titans scoring & game final.
 * - Uses the native Web Notifications API (no service worker / no VAPID needed).
 * - Client-side poller compares each snapshot with the last one and fires a
 *   native notification whenever the Titans score goes up or state → "post".
 * - Preference persists in localStorage.
 * - Silently no-op on iOS Safari <16.4 (no Notification API).
 *
 * Props:
 *   variant: "bar" | "hub" — small variant lives in LiveScoreBar; larger CTA
 *            variant lives on the Gameday Hub.
 */
const STORAGE_KEY = "ts_score_notifs_v1";

const supportsNotifications = () =>
  typeof window !== "undefined" && "Notification" in window;

const NAV_ICON = "/titan-standard-logo.webp";

export default function ScoreNotifier({ variant = "bar" }) {
  const [enabled, setEnabled] = useState(false);
  const [perm, setPerm] = useState(() => (supportsNotifications() ? Notification.permission : "unsupported"));
  const lastSnap = useRef({ titansScore: null, oppScore: null, state: null, eventId: null });

  // Hydrate from storage.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === "1" && supportsNotifications() && Notification.permission === "granted") {
        setEnabled(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Polling loop — 20s while enabled. Silent no-op otherwise.
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get("/live-scoreboard");
        const ev = data?.titans_event;
        if (alive && ev) {
          const titansHome = ev.home?.abbr === "TEN";
          const titans = titansHome ? ev.home : ev.away;
          const opp = titansHome ? ev.away : ev.home;
          const ts = Number(titans?.score ?? 0);
          const os_ = Number(opp?.score ?? 0);
          const st = ev.state;
          const prev = lastSnap.current;
          // First observed snapshot for this event → just seed, no notify.
          const sameEvent = prev.eventId === ev.id;
          if (sameEvent) {
            if (typeof prev.titansScore === "number" && ts > prev.titansScore) {
              const delta = ts - prev.titansScore;
              const kind = delta >= 6 ? "TOUCHDOWN!" : delta === 3 ? "Field Goal" : delta === 2 ? "Safety" : delta === 1 ? "PAT" : `+${delta}`;
              fireNotification(
                `Titans ${kind}`,
                `${opp?.abbr || "OPP"} ${os_} — TEN ${ts}${ev.clock ? `  ·  Q${ev.period} ${ev.clock}` : ""}`,
              );
            } else if (typeof prev.oppScore === "number" && os_ > prev.oppScore) {
              // Optional opponent score (soft cue, single line).
              fireNotification(
                `${opp?.abbr || "OPP"} scored`,
                `${opp?.abbr} ${os_} — TEN ${ts}${ev.clock ? `  ·  Q${ev.period} ${ev.clock}` : ""}`,
              );
            }
            if (prev.state && prev.state !== "post" && st === "post") {
              const titansWon = ts > os_;
              fireNotification(
                titansWon ? "TITANS WIN! 🏆" : "Final",
                `${ev.away?.abbr} ${ev.away?.score} — ${ev.home?.abbr} ${ev.home?.score}`,
              );
            }
          }
          lastSnap.current = { titansScore: ts, oppScore: os_, state: st, eventId: ev.id };
        }
      } catch { /* silent */ }
      timer = setTimeout(tick, 20_000);
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [enabled]);

  if (!supportsNotifications()) {
    // Render nothing in bar variant; small helper in hub variant.
    if (variant === "bar") return null;
    return (
      <div className="flex items-center gap-2 text-xs text-white/60" data-testid="notify-unsupported">
        <BellOff size={13} /> Browser notifications not supported on this device
      </div>
    );
  }

  const toggle = async () => {
    if (enabled) {
      setEnabled(false);
      try { localStorage.setItem(STORAGE_KEY, "0"); } catch { /* ignore */ }
      return;
    }
    let p = Notification.permission;
    if (p === "default") {
      p = await Notification.requestPermission();
      setPerm(p);
    }
    if (p === "granted") {
      setEnabled(true);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      fireNotification("You're subscribed", "We'll ping you when the Titans score or the game goes final.");
    }
  };

  if (variant === "bar") {
    const active = enabled && perm === "granted";
    return (
      <button
        type="button"
        onClick={toggle}
        data-testid="score-notify-toggle-bar"
        aria-pressed={active}
        title={active ? "Score alerts on" : "Get score alerts"}
        className={`inline-flex items-center gap-1 font-cond uppercase tracking-wider text-[10px] transition-opacity ${active ? "text-yellow-300" : "opacity-90 hover:opacity-100"}`}
      >
        {active ? <BellRing size={12} /> : <Bell size={12} />}
        <span className="hidden sm:inline">Alerts</span>
      </button>
    );
  }

  const active = enabled && perm === "granted";
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="score-notify-toggle-hub"
      aria-pressed={active}
      className={`inline-flex items-center gap-2 px-3.5 py-2 border font-cond uppercase tracking-widest text-[11px] transition-colors ${active ? "bg-titans-blue text-white border-titans-blue" : "bg-transparent text-white border-white/30 hover:border-white/60"}`}
    >
      {active ? <BellRing size={14} /> : <Bell size={14} />}
      {active ? "Score Alerts On" : "Notify Me When Titans Score"}
    </button>
  );
}

function fireNotification(title, body) {
  try {
    if (document.visibilityState === "visible") {
      // Optional in-page nod so users on the tab still see something.
      // We use a native notification anyway; browsers will show OS-level.
    }
    const n = new Notification(title, {
      body,
      icon: NAV_ICON,
      badge: NAV_ICON,
      tag: "titans-score",
      renotify: true,
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* ignore */ }
}
